import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, ObjectionsTrendResponse } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface ObjectionsTrendChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const formatPeriod = (period: string): string => {
  try {
    const date = new Date(period);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  } catch {
    return period;
  }
};

const ObjectionsTrendChart: React.FC<ObjectionsTrendChartProps> = ({
  businessIdentifier,
  timeRange,
}) => {
  const [data, setData] = useState<ObjectionsTrendResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const groupBy = timeRange === '90d' ? 'month' : 'week';
        const result = await analyticsService.getObjectionsTrend(
          businessIdentifier,
          timeRange || '7d',
          groupBy,
          5
        );
        setData(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch objections trend data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (!data || !data.data || data.data.length === 0) return {};

    // Collect unique periods and categories
    const allPeriods = Array.from(new Set(data.data.map((d) => d.period))).sort();
    const categories = data.top_categories || Array.from(new Set(data.data.map((d) => d.category)));
    const periodLabels = allPeriods.map(formatPeriod);

    // Build a lookup: category -> period -> count
    const lookup = new Map<string, Map<string, number>>();
    data.data.forEach((d) => {
      if (!lookup.has(d.category)) lookup.set(d.category, new Map());
      lookup.get(d.category)!.set(d.period, d.count);
    });

    const series = categories.map((cat, idx) => {
      const catMap = lookup.get(cat) || new Map();
      const values = allPeriods.map((p) => catMap.get(p) ?? 0);

      return {
        name: cat,
        type: 'line' as const,
        data: values,
        smooth: true,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {
          color: CHART_COLORS.palette[idx % CHART_COLORS.palette.length],
          width: 2,
        },
        itemStyle: {
          color: CHART_COLORS.palette[idx % CHART_COLORS.palette.length],
        },
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        textStyle: { fontFamily: FONT_FAMILY },
      },
      legend: {
        bottom: 0,
        left: 'center',
        textStyle: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#718096',
        },
        itemWidth: 14,
        itemHeight: 3,
        itemGap: 16,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: periodLabels,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        name: 'Count',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series,
    };
  };

  const hasData = data && data.data && data.data.length > 0;

  return (
    <ChartCard title="Objections Trend Over Time" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '350px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            No data available
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default ObjectionsTrendChart;
